import React from 'react';

/**
 * ParkWise Logo Component
 * Uses the official ParkWise Designated Space Management logo asset
 * 
 * Props:
 * - variant: 'full' | 'icon' - Full logo with text or just the P icon mark
 * - size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'kiosk' - Size preset
 * - className: Additional CSS classes
 */
export function ParkWiseLogo({ 
  variant = 'full', 
  size = 'md', 
  className = ''
}) {
  // Size presets - heights for the full logo (aspect ratio ~4:1)
  const sizes = {
    xs: { height: 24 },   // ~96px wide
    sm: { height: 32 },   // ~128px wide
    md: { height: 40 },   // ~160px wide
    lg: { height: 56 },   // ~224px wide
    xl: { height: 72 },   // ~288px wide
    kiosk: { height: 96 } // ~384px wide (for large kiosk screens)
  };

  // Icon sizes (square)
  const iconSizes = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 56,
    xl: 72,
    kiosk: 96
  };

  const s = sizes[size] || sizes.md;
  const iconSize = iconSizes[size] || iconSizes.md;

  if (variant === 'icon') {
    return (
      <div className={className} data-testid="parkwise-logo-icon">
        <img 
          src="/parkwise-icon-512.png" 
          alt="ParkWise"
          style={{ 
            height: iconSize, 
            width: iconSize,
            objectFit: 'contain'
          }}
        />
      </div>
    );
  }

  // Full logo with wordmark and tagline
  return (
    <div className={className} data-testid="parkwise-logo">
      <img 
        src="/parkwise-logo.png" 
        alt="ParkWise - Designated Space Management"
        style={{ 
          height: s.height, 
          width: 'auto',
          objectFit: 'contain'
        }}
        onError={(e) => {
          // Fallback if image fails to load
          e.target.onerror = null;
          e.target.style.display = 'none';
          e.target.parentNode.innerHTML = `
            <div style="display: flex; align-items: center; gap: 8px; height: ${s.height}px;">
              <span style="font-weight: bold; font-size: ${s.height * 0.4}px; color: #2563EB;">ParkWise</span>
            </div>
          `;
        }}
      />
    </div>
  );
}

/**
 * Simple icon-only logo for favicon/small spaces
 */
export function ParkWiseIcon({ size = 32, className = '' }) {
  return (
    <img 
      src="/parkwise-icon-192.png"
      alt="ParkWise"
      className={className}
      style={{ 
        width: size, 
        height: size,
        objectFit: 'contain'
      }}
      data-testid="parkwise-icon"
    />
  );
}

export default ParkWiseLogo;
